import { createElement, type CSSProperties, type ReactNode } from 'react';
import { App } from 'antd';
import type { NotificationInstance } from 'antd/es/notification/interface';
import type { MessageInstance } from 'antd/es/message/interface';
import { StatusSuccessIcon, StatusInfoIcon, StatusWarningIcon } from '../icons';

let notificationApi: NotificationInstance | null = null;

export const setNotificationApi = (api: NotificationInstance) => {
  notificationApi = api;
};

// Retained so NotificationBinder keeps compiling; `message.*` now renders via
// the notification API (below) for a consistent top-right card, so the antd
// message instance is intentionally unused.
export const setMessageApi = (_api: MessageInstance) => {};

export const notification: Pick<
  NotificationInstance,
  'success' | 'error' | 'info' | 'warning' | 'destroy'
> = {
  success: (args) => notificationApi?.success(args),
  error: (args) => notificationApi?.error(args),
  info: (args) => notificationApi?.info(args),
  warning: (args) => notificationApi?.warning(args),
  // Dismiss all, or one by key. Needed by any caller that shows a toast with
  // `duration: 0`, which never dismisses itself.
  destroy: (key) => notificationApi?.destroy(key),
};

/**
 * `message.*` calls render as top-right notification cards — identical layout,
 * placement and icons to `showNotification` — so every toast in the app is
 * visually consistent (no center-top antd message toasts).
 */
type ToastVariant = 'success' | 'error' | 'info' | 'warning';

const ICON_STYLE: Record<ToastVariant, CSSProperties> = {
  success: { color: 'var(--dz-success)', fontSize: 'var(--dz-font-size-2xl)' },
  error: { color: 'var(--dz-error)', fontSize: 'var(--dz-font-size-2xl)' },
  warning: { color: 'var(--dz-warning)', fontSize: 'var(--dz-font-size-2xl)' },
  info: { color: 'var(--dz-blue-muted)', fontSize: 'var(--dz-font-size-2xl)' },
};

const iconFor = (variant: ToastVariant): ReactNode => {
  if (variant === 'success') return createElement(StatusSuccessIcon, { style: ICON_STYLE.success });
  if (variant === 'error') return createElement(StatusWarningIcon, { style: ICON_STYLE.error });
  if (variant === 'warning') return createElement(StatusWarningIcon, { style: ICON_STYLE.warning });
  return createElement(StatusInfoIcon, { style: ICON_STYLE.info });
};

// message.* accepts (content, duration?, onClose?) or a config object.
const contentOf = (value: unknown): ReactNode =>
  value !== null && typeof value === 'object' && 'content' in value
    ? (value as { content: ReactNode }).content
    : (value as ReactNode);

// Only the `(content, duration?)` call form is supported — every call site uses
// it. antd's richer `message` surface (object config with `key`/`onClose`, the
// `onClose` 3rd arg, `message.loading`/`open`/`destroy`) is intentionally NOT
// forwarded; add explicit handling here if a caller ever needs it.
const showToast = (variant: ToastVariant, args: Parameters<MessageInstance['success']>) => {
  notificationApi?.[variant]({
    message: contentOf(args[0]),
    duration: typeof args[1] === 'number' ? args[1] : undefined,
    placement: 'topRight',
    icon: iconFor(variant),
  });
};

export const message = {
  success: (...args: Parameters<MessageInstance['success']>) => showToast('success', args),
  error: (...args: Parameters<MessageInstance['error']>) => showToast('error', args),
  info: (...args: Parameters<MessageInstance['info']>) => showToast('info', args),
  warning: (...args: Parameters<MessageInstance['warning']>) => showToast('warning', args),
};

export { App as AntdAppForNotification };
